/* Preserve merchant HTML; enhance native details without replacing its semantics. */
if (!customElements.get('aveyra-product-details')) {
  customElements.define('aveyra-product-details', class extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.initialized) {
        const description = this.querySelector('[data-editorial-description]');
        const specifications = this.querySelector('[data-product-specifications]');
        if (!description || !specifications) return;
        this.dataset.initialized = 'true';
        const nodes = [...description.childNodes];
        const specification = /^(?:dimensioni|misure|dimensions|materiale|material|design|chiusura|closure|interno|interior|manici|handles|tracolla|strap|finiture|hardware|metodo di trasporto|carrying method)\s*[:：]|^\d+(?:[.,]\d+)?\s*(?:cm\s*)?[x×]/i;
        const start = nodes.findIndex(node => {
          const text = node.textContent.trim();
          return specification.test(text) || (node.nodeType === 1 && ['UL', 'OL', 'TABLE'].includes(node.tagName) && [...node.querySelectorAll('li, tr')].some(item => specification.test(item.textContent.trim())));
        });
        if (start >= 0) {
          nodes.slice(start).forEach(node => specifications.append(node));
          this.querySelector('[data-specifications-row]').hidden = false;
        } // Unknown markup remains complete in Description.
      }
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.animations = new Map();
      window.addEventListener('scroll', () => this.restoreSticky(), { passive: true, signal: this.listeners.signal });
      this.querySelectorAll('details').forEach(details => {
        const summary = details.querySelector('summary');
        summary.setAttribute('aria-label', summary.textContent.trim());
        details.dataset.expanded = String(details.open);
        summary.setAttribute('aria-expanded', String(details.open));
        summary.addEventListener('click', event => {
          event.preventDefault();
          this.toggle(details, details.dataset.expanded !== 'true');
        }, { signal: this.listeners.signal });
      });
    }

    toggle(details, expanded) {
      // Native scroll anchoring must not follow a lower row as it moves.
      this.lockScrollAnchor();
      const wrapper = this.closest('.product__info-wrapper');
      if (wrapper && !wrapper.hasAttribute('data-accordion-flow') && getComputedStyle(wrapper).position === 'sticky') {
        // A growing sticky box is pushed UP by its containing block's bottom.
        // Preserve its current position in normal flow instead, allowing the
        // product section to grow DOWN with the content, even for long policies.
        this.stickyTop = parseFloat(getComputedStyle(wrapper).top);
        const offset = wrapper.getBoundingClientRect().top - wrapper.parentElement.getBoundingClientRect().top;
        wrapper.style.setProperty('--aveyra-accordion-flow-offset', `${Math.max(0, offset)}px`);
        wrapper.setAttribute('data-accordion-flow', '');
      }
      const summary = details.querySelector('summary');
      const content = summary.nextElementSibling;
      // Read the current interpolated state BEFORE cancelling, so rapid clicks
      // reverse smoothly from the visible height rather than jumping to an end.
      const fromHeight = details.getBoundingClientRect().height;
      const fromOpacity = details.open ? Number(getComputedStyle(content).opacity) : 0;
      this.animations.get(details)?.forEach(animation => animation.cancel());
      this.animations.delete(details);
      details.style.height = '';
      details.open = true; // Keep content in normal flow until collapse finishes.
      details.dataset.expanded = String(expanded);
      summary.setAttribute('aria-expanded', String(expanded));
      content.inert = !expanded;
      content.setAttribute('aria-hidden', String(!expanded));
      const style = getComputedStyle(details);
      const borderHeight = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
      const toHeight = expanded ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height + borderHeight;
      const finish = () => {
        details.open = expanded;
        details.style.height = '';
        details.removeAttribute('data-animating');
        this.animations.delete(details);
        if (!this.animations.size) {
          this.unlockScrollAnchor();
          this.restoreSticky();
        }
      };
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !details.animate) {
        finish();
        return;
      }
      details.dataset.animating = 'true';
      const options = { duration: 360, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' };
      const height = details.animate([{ height: `${fromHeight}px` }, { height: `${toHeight}px` }], options);
      const opacity = content.animate([{ opacity: fromOpacity }, { opacity: expanded ? 1 : 0 }], options);
      this.animations.set(details, [height, opacity]);
      height.onfinish = () => {
        if (this.animations.get(details)?.[0] === height) finish();
      };
    }

    lockScrollAnchor() {
      if (this.anchorLocked) return;
      this.anchorLocked = true;
      this.previousScrollAnchor = document.documentElement.style.overflowAnchor;
      document.documentElement.style.overflowAnchor = 'none';
    }

    unlockScrollAnchor() {
      if (!this.anchorLocked) return;
      document.documentElement.style.overflowAnchor = this.previousScrollAnchor;
      this.anchorLocked = false;
    }

    restoreSticky() {
      const wrapper = this.closest('.product__info-wrapper');
      if (!wrapper?.hasAttribute('data-accordion-flow') || this.animations.size || this.querySelector('details[data-expanded="true"]')) return;
      // If the customer scrolled down through expanded content, keep their
      // current position until scrolling back to the sticky threshold.
      if (wrapper.getBoundingClientRect().top < this.stickyTop - 1) return;
      wrapper.removeAttribute('data-accordion-flow');
      wrapper.style.removeProperty('--aveyra-accordion-flow-offset');
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.animations?.forEach((animations, details) => {
        animations.forEach(animation => animation.cancel());
        details.open = details.dataset.expanded === 'true';
        details.style.height = '';
        details.removeAttribute('data-animating');
      });
      this.animations?.clear();
      this.unlockScrollAnchor();
      const wrapper = this.closest('.product__info-wrapper');
      wrapper?.removeAttribute('data-accordion-flow');
      wrapper?.style.removeProperty('--aveyra-accordion-flow-offset');
    }
  });
}
